import { ReactNode } from 'react';

interface Props {
  /** Section number displayed in the avatar circle (1, 2, 3, ...) */
  number: number;
  /** Section title */
  title: ReactNode;
  /** Optional short instruction line rendered under the title */
  description?: ReactNode;
  /** Section body */
  children: ReactNode;
  /** Optional intercom target on the card container */
  intercomTarget?: string;
}

/**
 * Section card matching the Add Participant / Import Participants V2 pattern.
 * A rounded card with a numbered avatar circle in the top-left, a semibold
 * title, an optional muted description, and the section body below.
 *
 * Extracted here so the Add Group / Edit Group pages can share a single
 * visual language with Add Participant.
 */
function SectionCard({ number, title, description, children, intercomTarget }: Props) {
  return (
    <div
      className="bg-card border border-border rounded-lg p-8"
      data-intercom-target={intercomTarget}
    >
      <div className="flex items-start gap-4 mb-6">
        <div
          className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 text-lg"
          style={{ fontWeight: 'var(--font-weight-semibold)' }}
        >
          {number}
        </div>
        <div className="flex-1">
          <h2 className="text-xl mb-1" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {title}
          </h2>
          {description && <p className="text-muted-foreground">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default SectionCard;
